import { useState } from "react";
import { ShieldCheck, Globe2, FileSearch, Handshake } from "lucide-react";
import { useLang } from "../i18n";
import { Chapter, Reveal } from "./Reveal";

const WHY_ICONS = [ShieldCheck, Globe2, FileSearch, Handshake];

export function About() {
  const { t } = useLang();
  const [openExp, setOpenExp] = useState(null);
  return (
    <section id="about" data-testid="about-section" className="relative py-24 md:py-32 overflow-hidden">
      <div aria-hidden="true" className="absolute inset-0 overflow-hidden">
        <img src="/assets/bg-about.webp" alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#07111F]/80" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#07111F] via-transparent to-[#07111F]" />
      </div>
      <div className="relative mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <span className="font-display text-[8rem] leading-none text-transparent select-none" style={{ WebkitTextStroke: "1px rgba(201,162,39,0.35)" }} aria-hidden="true">02</span>
              <Chapter kicker={t.about.kicker} title={t.about.title} testId="about" />
            </div>
          </div>
          <div className="lg:col-span-8 flex flex-col gap-8 lg:pt-24">
            <Reveal>
              <div className="grid grid-cols-2 gap-4" data-testid="about-ingots">
                {[
                  { src: "/assets/mineral-gold.webp", alt: "Gold ingot with engraved MX seal", testId: "about-ingot-gold" },
                  { src: "/assets/mineral-silver.webp", alt: "Silver ingot with engraved MX seal", testId: "about-ingot-silver" },
                ].map((img) => (
                  <div key={img.testId} className="group relative overflow-hidden border border-[#C9A227]/15" style={{ borderRadius: "4px 20px 4px 20px" }}>
                    <img
                      data-testid={img.testId}
                      src={img.src}
                      alt={img.alt}
                      loading="lazy"
                      className="h-40 md:h-56 w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#07111F]/50 to-transparent" />
                  </div>
                ))}
              </div>
            </Reveal>
            {[t.about.p1, t.about.p2, t.about.p3].map((p, i) => (
              <Reveal key={i} delay={i * 0.12}>
                <p className={`leading-relaxed ${i === 0 ? "text-lg md:text-xl text-[#F5F7FA]" : "text-base text-[#EDF1F6]"} border-l-2 border-[#C9A227]/40 pl-6`} data-testid={`about-paragraph-${i + 1}`}>
                  {p}
                </p>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="mt-28">
          <Chapter kicker={t.why.kicker} title={t.why.title} testId="why" />
          <div className="grid sm:grid-cols-2 gap-5">
            {t.why.items.map((item, i) => {
              const Icon = WHY_ICONS[i];
              return (
                <Reveal key={i} delay={i * 0.1}>
                  <div
                    data-testid={`why-card-${i + 1}`}
                    className="group glass p-8 h-full transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-[0_16px_50px_rgba(201,162,39,0.1)]"
                    style={{ borderRadius: "4px 24px 4px 24px" }}
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-full border border-[#C9A227]/30 bg-[#C9A227]/10 text-[#C9A227] group-hover:bg-[#C9A227] group-hover:text-[#07111F] transition-[background-color,color] duration-400">
                      <Icon size={22} />
                    </div>
                    <h3 className="mt-5 font-display text-lg md:text-xl text-[#F5F7FA]">{item.t}</h3>
                    <p className="mt-3 text-sm text-[#EDF1F6] leading-relaxed">{item.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        <div className="mt-24" data-testid="expertise-section">
          <Reveal className="mb-8">
            <h3 className="font-display text-2xl md:text-3xl text-[#F5F7FA]" data-testid="expertise-title">{t.expertise.title}</h3>
            <p className="mt-2 text-sm md:text-base text-[#EDF1F6]">{t.expertise.subtitle}</p>
            <div className="mt-4 h-px w-16 bg-gradient-to-r from-[#C9A227] to-transparent" />
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {t.expertise.items.map((x, i) => (
              <Reveal key={x} delay={i * 0.05}>
                <button
                  data-testid={`expertise-item-${i + 1}`}
                  onClick={() => setOpenExp(openExp === i ? null : i)}
                  aria-expanded={openExp === i}
                  className="group relative glass w-full px-4 py-3.5 text-sm text-[#EDF1F6] flex items-center gap-2.5 text-left transition-[border-color,transform] duration-300 hover:border-[#C9A227]/40 hover:-translate-y-0.5"
                  style={{ borderRadius: "4px 14px 4px 14px" }}
                >
                  <span className="text-[#C9A227] text-[9px]" aria-hidden="true">◆</span>
                  {x}
                  <span
                    className={`pointer-events-none absolute bottom-full left-1/2 z-40 mb-3 w-52 -translate-x-1/2 transition-[opacity,transform] duration-300 ${
                      openExp === i ? "translate-y-0 scale-100 opacity-100" : "translate-y-2 scale-95 opacity-0 group-hover:translate-y-0 group-hover:scale-100 group-hover:opacity-100"
                    }`}
                    aria-hidden="true"
                  >
                    <img
                      src={`/assets/exp-${i}.webp`}
                      alt=""
                      loading="lazy"
                      className="h-32 w-full object-cover border border-[#C9A227]/30 shadow-[0_16px_50px_rgba(0,0,0,0.6)]"
                      style={{ borderRadius: "4px 14px 4px 14px" }}
                      data-testid={`expertise-img-${i + 1}`}
                    />
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
